import { AllergyChipInput, serializeAllergiesJson } from '@/components/AllergyChipInput';
import { MedicationEditor, serializeMedicationsJson } from '@/components/MedicationEditor';
import type { Medication } from '@/components/MedicationEditor';
import { createPet } from '@/lib/api';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Shared field wrapper
// ---------------------------------------------------------------------------

function Field({
  id,
  label,
  optional,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        {label}
        {optional && (
          <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">
            (optional)
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
      {title}
    </h2>
  );
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';
const selectCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';
const textareaCls =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30';

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface TextFormState {
  name: string;
  species: 'dog' | 'cat' | '';
  breed: string;
  sizeCategory: string;
  dateOfBirth: string;
  sex: string;
  neutered: string;
  color: string;
  microchip: string;
  microchipRegisteredWith: string;
  microchipRegistrationDate: string;
  notes: string;
  weightKg: string;
  feedingInstructions: string;
  medicalConditions: string;
  behaviorNotes: string;
  specialInstructions: string;
  insuranceProvider: string;
  insurancePolicyNumber: string;
  insuranceExpiresOn: string;
}

const EMPTY: TextFormState = {
  name: '',
  species: '',
  breed: '',
  sizeCategory: '',
  dateOfBirth: '',
  sex: '',
  neutered: '',
  color: '',
  microchip: '',
  microchipRegisteredWith: '',
  microchipRegistrationDate: '',
  notes: '',
  weightKg: '',
  feedingInstructions: '',
  medicalConditions: '',
  behaviorNotes: '',
  specialInstructions: '',
  insuranceProvider: '',
  insurancePolicyNumber: '',
  insuranceExpiresOn: '',
};

function toNullable(s: string): string | null {
  return s.trim() || null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PetNew() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<TextFormState>(EMPTY);
  const [allergies, setAllergies] = useState<string[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set(field: keyof TextFormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.species || !customerId) return;
    setBusy(true);
    setError(null);
    try {
      const weightKgParsed = form.weightKg.trim() ? Number(form.weightKg) : null;
      const neuteredParsed = form.neutered === '1' ? 1 : form.neutered === '0' ? 0 : null;

      const pet = await createPet({
        customerId,
        name: form.name.trim(),
        species: form.species as 'dog' | 'cat',
        breed: toNullable(form.breed),
        sizeCategory: toNullable(form.sizeCategory),
        dateOfBirth: toNullable(form.dateOfBirth),
        sex: toNullable(form.sex),
        neutered: neuteredParsed,
        color: toNullable(form.color),
        microchip: toNullable(form.microchip),
        microchipRegisteredWith: toNullable(form.microchipRegisteredWith),
        microchipRegistrationDate: toNullable(form.microchipRegistrationDate),
        notes: toNullable(form.notes),
        weightKg: weightKgParsed,
        feedingInstructions: toNullable(form.feedingInstructions),
        allergiesJson: serializeAllergiesJson(allergies),
        medicationsJson: serializeMedicationsJson(medications),
        medicalConditions: toNullable(form.medicalConditions),
        behaviorNotes: toNullable(form.behaviorNotes),
        specialInstructions: toNullable(form.specialInstructions),
        insuranceProvider: toNullable(form.insuranceProvider),
        insurancePolicyNumber: toNullable(form.insurancePolicyNumber),
        insuranceExpiresOn: toNullable(form.insuranceExpiresOn),
      });
      navigate(`/customers/${customerId}`, { replace: false, state: { petId: pet.id } });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
      <Link
        to={`/customers/${customerId}`}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
      >
        <ArrowLeft className="size-4" strokeWidth={1.75} />
        Back to customer
      </Link>

      <h1 className="mb-6 font-heading text-2xl font-medium tracking-tight">New pet</h1>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-8">
        {/* About this pet */}
        <section>
          <SectionHeader title="About this pet" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="pet-name" label="Name">
              <input
                id="pet-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                autoFocus
                className={inputCls}
              />
            </Field>
            <Field id="pet-species" label="Species">
              <select
                id="pet-species"
                required
                value={form.species}
                onChange={(e) => set('species', e.target.value)}
                className={selectCls}
              >
                <option value="">Select…</option>
                <option value="dog">Dog</option>
                <option value="cat">Cat</option>
              </select>
            </Field>
            <Field id="pet-breed" label="Breed" optional>
              <input
                id="pet-breed"
                type="text"
                value={form.breed}
                onChange={(e) => set('breed', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-size" label="Size category" optional>
              <select
                id="pet-size"
                value={form.sizeCategory}
                onChange={(e) => set('sizeCategory', e.target.value)}
                className={selectCls}
              >
                <option value="">Select…</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="extra_large">Extra large</option>
              </select>
            </Field>
            <Field id="pet-dob" label="Date of birth" optional>
              <input
                id="pet-dob"
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => set('dateOfBirth', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-sex" label="Sex" optional>
              <select
                id="pet-sex"
                value={form.sex}
                onChange={(e) => set('sex', e.target.value)}
                className={selectCls}
              >
                <option value="">Unknown</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </Field>
            <Field id="pet-neutered" label="Neutered / spayed" optional>
              <select
                id="pet-neutered"
                value={form.neutered}
                onChange={(e) => set('neutered', e.target.value)}
                className={selectCls}
              >
                <option value="">Unknown</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </Field>
            <Field id="pet-color" label="Colour" optional>
              <input
                id="pet-color"
                type="text"
                value={form.color}
                onChange={(e) => set('color', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-weight" label="Weight (kg)" optional>
              <input
                id="pet-weight"
                type="number"
                min="0"
                step="0.1"
                value={form.weightKg}
                onChange={(e) => set('weightKg', e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Microchip */}
        <section>
          <SectionHeader title="Microchip" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="pet-microchip" label="Chip number" optional>
              <input
                id="pet-microchip"
                type="text"
                value={form.microchip}
                onChange={(e) => set('microchip', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-chip-registry" label="Registered with" optional>
              <input
                id="pet-chip-registry"
                type="text"
                value={form.microchipRegisteredWith}
                onChange={(e) => set('microchipRegisteredWith', e.target.value)}
                placeholder="e.g. Petlog, Identibase"
                className={inputCls}
              />
            </Field>
            <Field id="pet-chip-date" label="Registration date" optional>
              <input
                id="pet-chip-date"
                type="date"
                value={form.microchipRegistrationDate}
                onChange={(e) => set('microchipRegistrationDate', e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Diet & feeding */}
        <section>
          <SectionHeader title="Diet & feeding" />
          <div className="grid grid-cols-1 gap-4">
            <Field id="pet-feeding" label="Feeding instructions" optional>
              <textarea
                id="pet-feeding"
                value={form.feedingInstructions}
                onChange={(e) => set('feedingInstructions', e.target.value)}
                rows={3}
                placeholder="Food name, portions, schedule…"
                className={textareaCls}
              />
            </Field>
            <div>
              <label
                htmlFor="pet-allergies"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Allergies
                <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">
                  (optional)
                </span>
              </label>
              <AllergyChipInput id="pet-allergies" value={allergies} onChange={setAllergies} />
              <p className="mt-1 text-xs text-muted-foreground">
                Type and press Enter or comma to add each allergy.
              </p>
            </div>
            <div>
              <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Medications
                <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">
                  (optional)
                </span>
              </p>
              <MedicationEditor value={medications} onChange={setMedications} />
            </div>
          </div>
        </section>

        {/* Behaviour & medical */}
        <section>
          <SectionHeader title="Behaviour & medical" />
          <div className="grid grid-cols-1 gap-4">
            <Field id="pet-conditions" label="Medical conditions" optional>
              <textarea
                id="pet-conditions"
                value={form.medicalConditions}
                onChange={(e) => set('medicalConditions', e.target.value)}
                rows={2}
                className={textareaCls}
              />
            </Field>
            <Field id="pet-behavior" label="Behaviour notes" optional>
              <textarea
                id="pet-behavior"
                value={form.behaviorNotes}
                onChange={(e) => set('behaviorNotes', e.target.value)}
                rows={3}
                placeholder="Separation anxiety, reactive to other dogs…"
                className={textareaCls}
              />
            </Field>
            <Field id="pet-special" label="Special instructions" optional>
              <textarea
                id="pet-special"
                value={form.specialInstructions}
                onChange={(e) => set('specialInstructions', e.target.value)}
                rows={2}
                placeholder="Grooming preferences, walking instructions…"
                className={textareaCls}
              />
            </Field>
            <Field id="pet-notes" label="General notes" optional>
              <textarea
                id="pet-notes"
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                rows={2}
                className={textareaCls}
              />
            </Field>
          </div>
        </section>

        {/* Insurance */}
        <section>
          <SectionHeader title="Insurance" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="pet-insurer" label="Provider" optional>
              <input
                id="pet-insurer"
                type="text"
                value={form.insuranceProvider}
                onChange={(e) => set('insuranceProvider', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-policy" label="Policy number" optional>
              <input
                id="pet-policy"
                type="text"
                value={form.insurancePolicyNumber}
                onChange={(e) => set('insurancePolicyNumber', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-insurance-expires" label="Policy expires" optional>
              <input
                id="pet-insurance-expires"
                type="date"
                value={form.insuranceExpiresOn}
                onChange={(e) => set('insuranceExpiresOn', e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        <div className="sticky bottom-0 -mx-4 border-t border-border bg-background/95 backdrop-blur px-4 py-3 md:-mx-8 md:px-8">
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy || !form.name.trim() || !form.species}
              className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-60"
            >
              {busy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
              {busy ? 'Saving…' : 'Add pet'}
            </button>
            <Link
              to={`/customers/${customerId}`}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary press"
            >
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
